import { AddressType, createReducer } from '@uplift/types';
import React from 'react';


export type AddressVerificationContextDataType = {
  shouldReverifyAddress: boolean;
  parsedVerifiedAddress: AddressType | null;
};

export type AddressVerificationContextStateType = {
  state: AddressVerificationContextDataType;
  dispatch: React.Dispatch<Action>;
};

type SetShouldReverifyAddressAction = {
  type: 'setShouldReverifyAddress';
  payload: {
    shouldReverifyAddress: boolean;
  };
};

type SetParsedVerifiedAddressAction = {
  type: 'setParsedVerifiedAddress';
  payload: {
    parsedVerifiedAddress: AddressType;
  };
};

type Action =
  | SetShouldReverifyAddressAction
  | SetParsedVerifiedAddressAction;

const initialState: AddressVerificationContextDataType = {
  shouldReverifyAddress: false,
  parsedVerifiedAddress: null,
};

export const addressVerificationReducer = createReducer(initialState, {
  setShouldReverifyAddress: (state, action: SetShouldReverifyAddressAction) => ({
    ...state,
    shouldReverifyAddress: action.payload.shouldReverifyAddress,
  }),
  setParsedVerifiedAddress: (state, action: SetParsedVerifiedAddressAction) => ({
    ...state,
    parsedVerifiedAddress: action.payload.parsedVerifiedAddress
  })
});


const addressVerificationContextDefaultValue = { state: initialState, dispatch: () => void (0) };

export const AddressVerificationContext = React.createContext<AddressVerificationContextStateType>(addressVerificationContextDefaultValue);

export const useAddressVerificationContext = () => {
  const addressVerificationContextValue = React.useContext(AddressVerificationContext);

  if (addressVerificationContextValue === addressVerificationContextDefaultValue) {
    throw new Error('useAddressVerificationContext must be used within a AddressVerificationProvider');
  }

  return addressVerificationContextValue;
};

export const AddressVerificationProvider: React.FC<AddressVerificationContextDataType> = ({ children, ...props }) => {
  const [ addressState, addressDispatch ] = React.useReducer(addressVerificationReducer, props);
  const addressCtx = React.useMemo(() => ({ state: addressState, dispatch: addressDispatch }), [ addressState, addressDispatch ]);

  return (
    <AddressVerificationContext.Provider value={addressCtx}>
      {children}
    </AddressVerificationContext.Provider>
  );
};

export const withAddressVerificationContext = <P extends object>(Component: React.ComponentType<P>) => {
  return (props: P) => {
    return (
      <AddressVerificationProvider shouldReverifyAddress={false} parsedVerifiedAddress={null}>
        <Component {...props} />
      </AddressVerificationProvider>
    );
  };
};